import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  FilterBar,
  FilterField,
  Input,
  PageShell,
  Pagination,
  RowActions,
  SegmentedControl,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
  type RowActionItem,
  type SegmentedOption,
} from '@/components/ui';
import type { AdminMemberSummary } from '../../hooks/useAdminUsersQuery';
import { memberStatusLabel } from '../../lib/userLabels';
import { useUserGradesQuery } from '../../grades/hooks/useUserGradesQuery';
import {
  useBusinessApproveMutation,
  useBusinessMembersQuery,
  useBusinessInfoFill,
  useBusinessRejectMutation,
  type BusinessMemberStatus,
} from '../hooks/useBusinessMembers';
import { BusinessApproveModal } from '../components/BusinessApproveModal';
import { BusinessRejectModal } from '../components/BusinessRejectModal';
import { BusinessDocumentModal } from '../components/BusinessDocumentModal';

const PAGE_SIZE = 20;

const STATUS_VARIANT: Record<BusinessMemberStatus, 'success' | 'warning' | 'danger'> = {
  PENDING_APPROVAL: 'warning',
  ACTIVE: 'success',
  REJECTED: 'danger',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ko-KR');
  } catch {
    return iso;
  }
};

/** 뮤테이션 에러 → 표면화 메시지(BE 한국어 메시지는 throwFromResponse 가 Error.message 로 전달). */
const errorMessageOf = (err: Error | null): string | null => err?.message ?? null;

type ModalState =
  | { kind: 'approve'; member: AdminMemberSummary }
  | { kind: 'reject'; member: AdminMemberSummary }
  // 사업자등록증 보기 — 모든 상태(승인 완료·반려 포함).
  | { kind: 'document'; member: AdminMemberSummary }
  | null;

export const BusinessMemberListPage = () => {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<BusinessMemberStatus>('PENDING_APPROVAL');
  const [keyword, setKeyword] = useState('');
  const [inputValue, setInputValue] = useState('');
  // QA-430 — 승인 완료 탭 등급 다중 선택(체크박스). 다른 탭에서는 쓰지 않는다(승인 전·반려는 등급 없음).
  const [gradeIds, setGradeIds] = useState<ReadonlyArray<string>>([]);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<ModalState>(null);

  const query = useBusinessMembersQuery({
    status,
    keyword: keyword || undefined,
    gradeIds: status === 'ACTIVE' && gradeIds.length > 0 ? gradeIds : undefined,
    page,
    size: PAGE_SIZE,
  });
  const approveMutation = useBusinessApproveMutation();
  const rejectMutation = useBusinessRejectMutation();

  // QA-382 — 목록 응답에 없는 상호·사업자등록번호를 상세로 채운다.
  const items = useBusinessInfoFill(query.data?.items ?? []);
  const total = query.data?.meta.total ?? 0;

  const statusOptions = useMemo<ReadonlyArray<SegmentedOption<BusinessMemberStatus>>>(
    () => [
      { value: 'PENDING_APPROVAL', label: t('businessMembers.tab.pending', '승인 대기') },
      { value: 'ACTIVE', label: t('businessMembers.tab.approved', '승인 완료') },
      { value: 'REJECTED', label: t('businessMembers.tab.rejected', '반려') },
    ],
    [t],
  );

  const handleSearch = useCallback(() => {
    setKeyword(inputValue);
    setPage(1);
  }, [inputValue]);

  const handleReset = useCallback(() => {
    setGradeIds([]);
    setInputValue('');
    setKeyword('');
    setPage(1);
  }, []);

  const closeModal = useCallback(() => setModal(null), []);

  const handleApprove = useCallback(
    (gradeId: string | null) => {
      if (modal?.kind !== 'approve') return;
      const { userId } = modal.member;
      approveMutation.mutate(
        { userId, gradeId },
        {
          onSuccess: () => {
            toast.success(t('businessMembers.approve.toast', '사업자회원이 승인되었습니다'));
            closeModal();
          },
        },
      );
    },
    [approveMutation, closeModal, modal, t],
  );

  const handleReject = useCallback(
    (reason: string | null) => {
      if (modal?.kind !== 'reject') return;
      const { userId } = modal.member;
      rejectMutation.mutate(
        { userId, reason },
        {
          onSuccess: () => {
            toast.success(t('businessMembers.reject.toast', '사업자회원이 반려되었습니다'));
            closeModal();
          },
        },
      );
    },
    [rejectMutation, closeModal, modal, t],
  );

  // QA-430 — 등급 칸: 목록의 gradeId → 등급명.
  const gradesQuery = useUserGradesQuery({ page: 1, size: 100 });
  const gradeNameById = useMemo(
    () => new Map((gradesQuery.data?.content ?? []).map((g) => [g.gradeId, g.gradeName] as const)),
    [gradesQuery.data],
  );

  const columns = useMemo<ReadonlyArray<DataTableColumn<AdminMemberSummary>>>(
    () => [
      {
        // QA-430 — 회원 ID(로그인 ID). 로그인 ID 없이 가입한 회원은 '—'.
        key: 'loginId',
        header: t('businessMembers.cols.loginId', '회원 ID'),
        cell: (row) => (
          <span className="text-[12px]" style={{ color: 'var(--content-default)' }} data-testid="business-member-login-id">
            {row.loginId?.trim() || '—'}
          </span>
        ),
        width: 130,
      },
      {
        key: 'name',
        header: t('businessMembers.cols.name', '담당자'),
        // 회원 상세로 이동 — 상세 "기본 정보" 탭에 사업자 정보(대표자·업태·종목·사업장 주소)와 사업자등록증이 있다.
        cell: (row) => (
          <Link
            to={`/users/members/${row.userId}`}
            className="font-medium underline-offset-2 hover:underline"
            style={{ color: 'var(--content-strong)' }}
            data-testid="business-member-detail-link"
          >
            {row.name || '—'}
          </Link>
        ),
        width: 120,
      },
      {
        key: 'businessName',
        header: t('businessMembers.cols.businessName', '상호'),
        cell: (row) => (
          <Link
            to={`/users/members/${row.userId}`}
            className="text-[12px] underline-offset-2 hover:underline"
            style={{ color: 'var(--content-default)' }}
          >
            {row.businessName || '—'}
          </Link>
        ),
        width: 160,
      },
      {
        key: 'businessRegistrationNo',
        header: t('businessMembers.cols.businessRegistrationNo', '사업자등록번호'),
        cell: (row) => (
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-default)' }}>
            {row.businessRegistrationNo || '—'}
          </span>
        ),
        width: 140,
      },
      {
        key: 'email',
        header: t('businessMembers.cols.email', '이메일'),
        cell: (row) => (
          <span
            className="block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-[12px]"
            style={{ color: 'var(--content-default)' }}
            title={row.email}
          >
            {row.email}
          </span>
        ),
      },
      {
        key: 'grade',
        header: t('businessMembers.cols.grade', '등급'),
        cell: (row) => (
          <span className="text-[12px] whitespace-nowrap" style={{ color: 'var(--content-default)' }} data-testid="business-member-grade">
            {row.gradeName?.trim() || (row.gradeId && gradeNameById.get(row.gradeId)) || '—'}
          </span>
        ),
        width: 110,
      },
      {
        key: 'status',
        header: t('businessMembers.cols.status', '상태'),
        cell: (row) => (
          <StatusBadge
            label={memberStatusLabel(t, row.status, 'BUSINESS')}
            variant={STATUS_VARIANT[row.status as BusinessMemberStatus] ?? 'neutral'}
          />
        ),
        width: 104,
      },
      {
        key: 'createdAt',
        header: t('businessMembers.cols.createdAt', '신청일'),
        cell: (row) => (
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {formatDate(row.createdAt)}
          </span>
        ),
        width: 112,
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        width: 56,
        cell: (row) => {
          // 서류 보기는 모든 상태에서(종전엔 승인 모달에서만 볼 수 있어 승인 완료·반려 회원은 다시 볼 방법이 없었다).
          const actions: RowActionItem[] = [
            {
              key: 'document',
              label: t('businessMembers.actions.document', '사업자등록증 보기'),
              onClick: () => setModal({ kind: 'document', member: row }),
            },
            ...(row.status === 'PENDING_APPROVAL'
              ? ([
                  {
                    key: 'approve',
                    label: t('businessMembers.actions.approve', '승인'),
                    separatorBefore: true,
                    onClick: () => setModal({ kind: 'approve', member: row }),
                  },
                  {
                    key: 'reject',
                    label: t('businessMembers.actions.reject', '반려'),
                    variant: 'danger',
                    onClick: () => setModal({ kind: 'reject', member: row }),
                  },
                ] satisfies RowActionItem[])
              : []),
          ];
          return (
            <RowActions
              triggerLabel={`${row.businessName || row.name} ${t('businessMembers.actions.menu', '액션')}`}
              actions={actions}
            />
          );
        },
      },
    ],
    [t, gradeNameById],
  );

  return (
    <PageShell title={t('businessMembers.title', '사업자회원 관리')} data-testid="business-member-list-page">
      <div className="mb-3">
        <SegmentedControl<BusinessMemberStatus>
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={statusOptions}
          aria-label={t('businessMembers.statusFilter', '상태 필터')}
        />
      </div>

      <FilterBar
        dense
        filters={
          <>
          {status === 'ACTIVE' ? (
            <FilterField label={t('businessMembers.filter.grade', '등급')}>
              <div className="flex flex-wrap items-center gap-3 text-[13px]" role="group" aria-label={t('businessMembers.filter.grade', '등급')} data-testid="business-grade-filter">
                {(gradesQuery.data?.content ?? [])
                  .filter((g) => g.active)
                  .map((g) => (
                    <label key={g.gradeId} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={gradeIds.includes(g.gradeId)}
                        onChange={() => {
                          setGradeIds((prev) =>
                            prev.includes(g.gradeId) ? prev.filter((x) => x !== g.gradeId) : [...prev, g.gradeId],
                          );
                          setPage(1);
                        }}
                      />
                      {g.gradeName}
                    </label>
                  ))}
              </div>
            </FilterField>
          ) : null}
          <FilterField label={t('businessMembers.filter.keyword', '검색어')}>
            <Input
              type="search"
              size="sm"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              placeholder={t('businessMembers.filter.keywordPlaceholder', '상호 · 담당자 · 이메일 · 회원ID')}
              aria-label={t('businessMembers.filter.keywordPlaceholder', '상호 · 담당자 · 이메일 · 회원ID')}
            />
          </FilterField>
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleSearch}>
              {t('businessMembers.filter.search', '검색')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset}>
              {t('businessMembers.filter.reset', '초기화')}
            </Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('businessMembers.loadFailed', '목록을 불러오지 못했습니다')}</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('businessMembers.retry', '재시도')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...items]}
          rowKey={(row) => row.userId}
          stickyHeader
          mobileCardCollapse
          emptyState={t('businessMembers.empty', '해당 상태의 사업자회원이 없습니다')}
          ariaLabel={t('businessMembers.title', '사업자회원 관리')}
        />
      )}

      <Pagination
        total={total}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />

      <BusinessApproveModal
        open={modal?.kind === 'approve'}
        memberName={modal?.kind === 'approve' ? (modal.member.businessName || modal.member.name) : ''}
        userId={modal?.kind === 'approve' ? modal.member.userId : undefined}
        documentUrl={modal?.kind === 'approve' ? modal.member.businessDocumentUrl : undefined}
        isSubmitting={approveMutation.isPending}
        errorMessage={approveMutation.isError ? errorMessageOf(approveMutation.error) : null}
        onConfirm={handleApprove}
        onCancel={closeModal}
      />

      <BusinessDocumentModal
        open={modal?.kind === 'document'}
        userId={modal?.kind === 'document' ? modal.member.userId : undefined}
        memberName={modal?.kind === 'document' ? (modal.member.businessName || modal.member.name) : ''}
        documentUrl={modal?.kind === 'document' ? modal.member.businessDocumentUrl : undefined}
        onClose={closeModal}
      />

      <BusinessRejectModal
        open={modal?.kind === 'reject'}
        memberName={modal?.kind === 'reject' ? (modal.member.businessName || modal.member.name) : ''}
        isSubmitting={rejectMutation.isPending}
        errorMessage={rejectMutation.isError ? errorMessageOf(rejectMutation.error) : null}
        onConfirm={handleReject}
        onCancel={closeModal}
      />
    </PageShell>
  );
};
